import React, { useCallback, useEffect, useState } from 'react';
import { aiSettingsService } from '../../../services/aiSettingsService';
import AiKeyPanel from './AiKeyPanel';

const ModeOption = ({ active, disabled, title, text, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`text-left rounded-xl border p-4 transition disabled:opacity-60 ${
      active ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'
    }`}
  >
    <span className="block font-semibold text-slate-900">{title}</span>
    <span className="mt-1 block text-sm text-slate-600">{text}</span>
  </button>
);

const AiSettings = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [wantsOwn, setWantsOwn] = useState(false);

  const run = useCallback(async (fn) => {
    setBusy(true);
    setError('');
    try {
      setStatus(await fn());
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    run(aiSettingsService.status);
  }, [run]);

  const chooseOwn = () => {
    if (status.connected && status.keyStatus === 'valid') run(() => aiSettingsService.setMode('byok'));
    else setWantsOwn(true);
  };

  const useMeetoi = () => {
    setWantsOwn(false);
    run(() => aiSettingsService.setMode('meetoi'));
  };

  const byok = status?.mode === 'byok';

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
      <h3 className="text-lg font-bold text-slate-900 mb-1">AI Settings</h3>
      <p className="text-sm text-slate-600 mb-4">Choose what powers Meetoi's AI features, including Moments AI and Review My Networking.</p>
      {error && <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {!status && !error && <p className="text-sm text-slate-500">Loading...</p>}
      {status && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <ModeOption
              active={!byok && !wantsOwn}
              disabled={busy}
              title="Meetoi AI"
              text="Works out of the box. Nothing to set up."
              onClick={useMeetoi}
            />
            <ModeOption
              active={byok || wantsOwn}
              disabled={busy || !status.byokAvailable}
              title="My own OpenAI key"
              text={status.byokAvailable ? 'Requests use your key and are billed to your OpenAI account.' : 'Not available on this server yet.'}
              onClick={chooseOwn}
            />
          </div>
          {(byok || wantsOwn) && (
            <AiKeyPanel
              status={status}
              busy={busy}
              onSave={(key) => run(() => aiSettingsService.saveKey(key))}
              onRecheck={() => run(aiSettingsService.recheck)}
              onRemove={() => { setWantsOwn(false); return run(aiSettingsService.removeKey); }}
            />
          )}
          <p className="mt-4 text-xs text-slate-500">
            When you use your own key, OpenAI charges your account directly and you are responsible for those costs. If your key stops working, Meetoi shows an error and never switches to Meetoi AI on its own.
          </p>
        </>
      )}
    </div>
  );
};

export default AiSettings;